import React, { useState } from 'react';
import { Eye, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import './vStyles/VolunteerProjects.css';

interface Task {
  id: number;
  title: string;
  description: string;
  project_id: number;
  project_name?: string;
  required_skills: string[];
  location: string;
  due_date?: string;
  applied?: boolean;
  capacity?: number;
  participant_count?: number;
}

interface TaskCardProps {
  task: Task;
  onApply: (taskId: number) => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const TaskCard: React.FC<TaskCardProps> = ({ task, onApply }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [applying, setApplying] = useState(false);
  const { volunteerId } = useAuth();

  const handleApply = async () => {
    setApplying(true);
    try {
      const currentVolunteerId = volunteerId ? volunteerId.toString() : '1';
      
      const response = await axios.post(`${API_BASE_URL}/api/tasks/${task.id}/apply`, {
        volunteerId: currentVolunteerId
      }, {
        headers: {
          'user-id': currentVolunteerId,
          'Content-Type': 'application/json'
        }
      });
      
      onApply(task.id);
      
      const data = response.data as { joinedProject?: boolean };
      if (data.joinedProject) {
        alert('Application submitted successfully! You have been automatically joined to the project.');
      } else {
        alert('Application submitted successfully!');
      }
    } catch (error) {
      console.error('Error applying for task:', error);
      alert('Failed to apply for task. Please try again.');
    } finally {
      setApplying(false);
    }
  };


  function isApplied() {
    return !!task.applied;
  }

  return (
    <div className="task-card">
      <div className="task-header">
        <div className="task-info">
          <h4>{task.title}</h4>
          {task.project_name && <span className="project-name">Project: {task.project_name}</span>}
          <div className="task-skills">
            {task.required_skills?.map(skill => (
              <span key={skill} className="skill-tag">{skill}</span>
            ))}
          </div>
        </div>
        
        <div className="task-actions">
          <button 
            onClick={() => setShowDetails(!showDetails)}
            className="view-details-btn"
          >
            <Eye size={14} /> {showDetails ? 'Hide' : 'View'}
          </button>
          
          {isApplied() ? (
            <button className="applied-btn" disabled>
              <CheckCircle size={14} /> Applied
            </button>
          ) : (
            <button 
              onClick={handleApply}
              disabled={applying}
              className="apply-btn"
            >
              {applying ? 'Applying...' : 'Apply for Task'}
            </button>
          )}
        </div>
      </div>

      {showDetails && (
        <div className="task-details">
          <p>{task.description}</p>
          <div className="task-meta">
            {task.location && <span>📍 {task.location}</span>}
            {task.due_date && (
              <span>📅 Due: {new Date(task.due_date).toLocaleDateString()}</span>
            )}
            {task.capacity && (
              <span className="capacity-info">
                👥 {task.participant_count || 0}/{task.capacity} participants
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCard;